/* global google */
import React, { useCallback, useMemo } from 'react'
import { Marker } from '@react-google-maps/api'
import useToggle from 'hooks/useToggle'
import type { PinSizing } from './Pins'
import ExperiencePins from './Pins'
import config from 'constants/config'

function scaleDownSizing(sizing: PinSizing): PinSizing {
  const SCALE = 5 / 6
  const { size, anchor } = sizing
  return {
    size: [size[0] * SCALE, size[1] * SCALE],
    anchor: [anchor[0] * SCALE, anchor[1] * SCALE],
  }
}

const MAX_PIN_LIMIT = config.search.mapView.pinLimit

interface Props {
  marker: App.SearchExperienceMapMarker
  onClick: (marker: App.SearchExperienceMapMarker) => void
  scaleDown?: boolean
  onLoad: (markerInstance: google.maps.Marker, markerData: App.SearchExperienceMapMarker) => void
  onUnmount?: (markerInstance: google.maps.Marker) => void
  active?: boolean
  visited?: boolean
  isHovered?: boolean
  index?: number
}

function SearchMapMarker(props: Props) {
  const {
    active,
    visited,
    isHovered,
    marker,
    scaleDown,
    onClick,
    onLoad,
    onUnmount,
    index = 0,
  } = props

  const { value: hovered, on: hoverOn, off: hoverOff } = useToggle()

  const calculateZIndex = useCallback(
    (marker: App.SearchExperienceMapMarker, index: number) => {
      if (active) {
        return MAX_PIN_LIMIT + 1
      }
      if (isHovered || hovered) {
        return MAX_PIN_LIMIT
      }
      if (index >= MAX_PIN_LIMIT) {
        return 0
      }
      return MAX_PIN_LIMIT - index
    },
    [active, isHovered, hovered],
  )

  // Get the first category from all the experienceItems on that marker or 999 as a fallback pin
  const PinConfig = useMemo(() => {
    const pinConfigId = marker.experienceItems?.map(item => item.primaryCategory?.parentId)[0] || 999
    // sometimes pinConfigId is undefined, so we use 999 as a fallback
    // but we need to make sure that the pinConfigId is a valid key in the ExperiencePins object, if not we use 999 as a second fallback
    const pinConfig = ExperiencePins[pinConfigId] || ExperiencePins[999]
    return pinConfig
  }, [marker.experienceItems])

  let pinVersion = PinConfig.default
  if (active) {
    pinVersion = PinConfig.active
  } else if (visited) {
    pinVersion = PinConfig.previewed
  }

  const pin = (hovered || isHovered) ? pinVersion.hover : pinVersion.default
  const { image, sizing: fullSizing } = pin

  const sizing = scaleDown ? scaleDownSizing(fullSizing) : fullSizing

  const iconSize = new google.maps.Size(...sizing.size)
  const anchor = new google.maps.Point(...sizing.anchor)
  const position = { lat: marker.latitude, lng: marker.longitude }
  const iconObject = { url: `${image}`, scaledSize: iconSize, anchor }

  const zIndex = useMemo(() => calculateZIndex(marker, index), [calculateZIndex, index, marker])

  const loadHandler = useCallback((markerInstance: google.maps.Marker) => {
    onLoad(markerInstance, marker)
  }, [marker, onLoad])

  const clickHandler = useCallback(() => {
    onClick(marker)
  }, [onClick, marker])

  return (
    <Marker
      position={position}
      icon={iconObject}
      onLoad={loadHandler}
      onUnmount={onUnmount}
      onMouseOver={hoverOn}
      onMouseOut={hoverOff}
      onClick={clickHandler}
      zIndex={zIndex}
    />
  )
}

export default React.memo(SearchMapMarker)
